import type { Being, BeingState } from '../../../models/Being';
import type { Surroundings } from '../../../models/Surroundings';
import type { IBeingState } from './IBeingState';
import { AI, DirectionsVectors } from '../../../models/constants';
import { directionToward } from '../scoring';
import { EatingState } from './EatingState';
import { RestingState } from './RestingState';
import { FleeingState } from './FleeingState';
import { ExploringState } from './ExploringState';

export class HuntingState implements IBeingState {
    static readonly stateName = 'Hunting';

    /** Probability per tick that a Being enters this state when a Beast is nearby. */
    static readonly HUNT_PROBABILITY = 0.4;

    /** Damage dealt to the target Beast each tick. */
    static readonly DAMAGE_PER_TICK = 0.05;

    /** Squared distance (grid units²) the Being holds from its target. Within this
     *  range it stops advancing and attacks; outside it, it closes in and deals no
     *  damage. 36 = a 6-grid-point throwing radius.
     */
    static readonly HUNT_RANGE_SQ = 36;

    /** Health fraction below which the Being breaks off the hunt and forages. */
    static readonly STOP_HUNT_HEALTH = AI.HUNGER_THRESHOLD;

    /** Health fraction a Being must exceed to *start* a hunt. Deliberately higher
     *  than `STOP_HUNT_HEALTH` so the two form a hysteresis band: a Being that has
     *  just broken off exhausted must forage back up to a comfortable margin before
     *  it will rejoin, instead of re-entering at 0.31 health for a single useless
     *  tick. Only a well-fed herd can mount a sustained hunt. */
    static readonly START_HUNT_HEALTH = 0.6;

    get allowInterrupt(): boolean {
        return true;
    }

    canEnter(being: Being, surroundings: Surroundings): boolean {
        if (being.state === EatingState.stateName) return false;
        if (being.state === RestingState.stateName) return false;
        if (being.state === FleeingState.stateName) return false;
        if (surroundings.nearbyBeasts.length === 0) return false;
        if (being.health < HuntingState.START_HUNT_HEALTH) return false;
        return Math.random() < HuntingState.HUNT_PROBABILITY;
    }

    tick(being: Being, surroundings: Surroundings): BeingState {
        // Danger takes priority — hand off to FleeingState interrupt
        if (surroundings.isInDanger) return FleeingState.stateName;

        // Too weak to keep fighting — break off and forage
        if (being.health < HuntingState.STOP_HUNT_HEALTH) return ExploringState.stateName;

        const target = surroundings.nearbyBeasts.find(b => b.health > 0);
        if (!target) return ExploringState.stateName;

        // Pursue for as long as the hunt lasts, and throw whenever within range.
        // The Being must keep closing even while attacking: the target flees faster
        // than the Being pursues (`BEAST.FLEE_SPEED` > 1.0), so holding position at
        // the standoff radius simply lets the quarry walk out of range and away
        // forever. Damage still only lands inside `HUNT_RANGE_SQ`, so only Beings
        // that actually committed to the charge contribute to the kill.
        const dx = target.x - being.x;
        const dz = target.z - being.z;
        if (dx * dx + dz * dz <= HuntingState.HUNT_RANGE_SQ) {
            // damage() opens the kill-credit window; the Beast attributes the kill.
            target.damage(HuntingState.DAMAGE_PER_TICK);
        }

        const dir = directionToward(being, target);
        being.moveDirection = dir;
        const vec = DirectionsVectors.get(dir)!;
        being.translate(vec.dx, vec.dz);

        return HuntingState.stateName;
    }
}
